import { parentFolderOfPath, isFileLikeDatasourceType } from 'Utils/fileTypeUtils';
import { getSourceNomenclature } from 'Utils/nomenclature';
import { isRemediable, countUnitForSource, countFindings, diffTotalsIn } from './edlUtils';

/**
 * "Where inside a data source" — the level between the Enterprise landscape's per-source tiles
 * and the per-(location, entity) findings ledger. An AREA is one table (relational), one
 * immediate-parent folder (file sources) or one collection (NoSQL).
 *
 * Every derivation here reads the diff row's SEPARATE `schema` / `table` / `field` / `filePath`
 * fields, never the concatenated `location` string. `location` is ambiguous by construction:
 * typed-file rows join the file path and the column with a '.' ("payroll/2024/logins.csv.pwd"),
 * and NoSQL field names are themselves dotted JSON paths ("db.coll.contact.email"). There is no
 * delimiter you can safely split on. See FindingsDiffResponse in c2-discover-api.
 *
 * File paths also arrive in TWO forms within a single run, and are normalized here to one.
 * The worker stores a structured finding's path root-RELATIVE (it is part of
 * `discovery_finding`'s primary key, so it stays compact and collision-free) but a document
 * span's path ABSOLUTE (`discovery_span_finding.file_path` is the address masking-service later
 * reopens the file by). One physical folder therefore arrives as both
 * "sharepoint://host/sites/.../testing/docs/Finance" and "docs/Finance". Stripping the data
 * source's own `rootPath` collapses them onto the relative form — the two sets of findings are
 * disjoint files in the same folder, so this is a union, never double-counting.
 */

export const AREA_KIND = { TABLE: 'TABLE', FOLDER: 'FOLDER', COLLECTION: 'COLLECTION' };

/** Label for a folder area that sits directly under the scan root (parentFolderOfPath → ''). */
export const ROOT_AREA_LABEL = '(root)';

const LEVEL_RANK = { CRITICAL: 0, HIGH: 1, MEDIUM: 2, LOW: 3 };

const trimTrailingSlash = (p) => String(p || '').replace(/[/\\]+$/, '');

/**
 * A data source's scan root, from the globally loaded connections. Mirrors the accessor
 * CandidateSourcesPanel uses — the field rides in `config.rootPath` on a stored connection and
 * at the top level on a freshly mapped one.
 */
export function rootPathOf(source) {
  return trimTrailingSlash(source?.rootPath || source?.config?.rootPath || '');
}

/**
 * Make an absolute worker path root-relative. Mirrors the worker's own
 * `FileGenericRunService.relativePath`: exact prefix match, then drop one separator. A path that
 * does not sit under the root (or a source with no known root) is returned untouched, so a bad
 * or missing rootPath degrades to today's behaviour rather than mangling the path.
 */
export function relativizePath(path, rootPath) {
  const p = String(path || '');
  const root = trimTrailingSlash(rootPath);
  if (!root || !p.startsWith(root)) return p;
  const tail = p.slice(root.length);
  return tail.replace(/^[/\\]+/, '');
}

/**
 * Which noun a source's areas are. Mirrors the worker's own categorisation:
 * file-based DatasourceTypes group by folder, NOSQL by collection, everything else by table.
 * `isFileLikeDatasourceType` (not the strict mirror) is right here because this is a LABELLING
 * decision — a legacy FILE_FOLDER row is still a folder of files to the user.
 */
export function areaKindForSource(source) {
  const type = source?.datasourceType;
  if (isFileLikeDatasourceType(type)) return AREA_KIND.FOLDER;
  if (type === 'NOSQL') return AREA_KIND.COLLECTION;
  return AREA_KIND.TABLE;
}

/** Heading noun for a source's areas — "tables" / "folders" / "collections". */
export function areaNoun(source, { plural = true } = {}) {
  const kind = areaKindForSource(source);
  if (kind === AREA_KIND.FOLDER) return plural ? 'folders' : 'folder';
  if (kind === AREA_KIND.COLLECTION) return plural ? 'collections' : 'collection';
  // Relational wording comes from the shared nomenclature so DynamoDB et al. stay consistent
  // with the rest of the product rather than hard-coding "tables" a second time.
  const n = getSourceNomenclature(source?.engine, source?.datasourceType);
  return plural ? n.item : n.itemSingular;
}

export function areaLabel(key, kind) {
  if (kind === AREA_KIND.FOLDER && !key) return ROOT_AREA_LABEL;
  return key || ROOT_AREA_LABEL;
}

/**
 * The two parts of an area's identity, kept SEPARATE so a view can put the qualifier on a second
 * line instead of rendering "schema.table_name" as one crowded string:
 *
 *   name       what this area is called — the table, the collection, the folder
 *   qualifier  what it sits in — the schema, the NoSQL database, the parent folder path
 *
 * Built while grouping (from the row's own `schema` / `table` fields), never by splitting the
 * area key — see the note at the top of this file on why `location` cannot be split.
 */
const areaParts = (kind, schema, table, key) => {
  if (kind === AREA_KIND.FOLDER) {
    const parts = String(key || '').split('/').filter(Boolean);
    if (parts.length === 0) return { name: ROOT_AREA_LABEL, qualifier: '' };
    return { name: parts[parts.length - 1], qualifier: parts.slice(0, -1).join('/') };
  }
  if (!table) return { name: areaLabel(key, kind), qualifier: '' };
  return { name: table, qualifier: schema || '' };
};

/**
 * The area one diff row belongs to.
 *
 * Order matters — a typed-file row is a FIELD row whose `table` holds the RELATIVE FILE PATH
 * (schema empty, filePath null), so it must be folder-grouped before the relational fallback.
 *
 * - locationKind FILE (document / Folder Search spans) → parent folder of the absolute filePath
 * - FIELD on a file-based source (CSV/Parquet/JSON/XML/Excel/fixed-width/HL7/EDI) → parent
 *   folder of `table`
 * - FIELD on NOSQL → "database.collection" (or the bare collection when the db is absent)
 * - FIELD otherwise (relational) → "schema.table" (or the bare table when schema is empty —
 *   engines that record no owner store "")
 */
export function areaOf(row, source) {
  const kind = areaKindForSource(source);

  const root = rootPathOf(source);

  if (row?.locationKind === 'FILE') {
    // For FILE rows the worker's RowKey.location() returns the file path verbatim, so `location`
    // is an exact stand-in when `filePath` is absent (older payloads, trimmed fixtures).
    // These arrive ABSOLUTE; relativizing lands them on the same key as the structured findings
    // for the same folder.
    const key = parentFolderOfPath(relativizePath(row.filePath || row.location || '', root));
    return {
      key,
      kind: AREA_KIND.FOLDER,
      label: areaLabel(key, AREA_KIND.FOLDER),
      ...areaParts(AREA_KIND.FOLDER, '', '', key),
    };
  }

  if (kind === AREA_KIND.FOLDER) {
    // Already relative in practice; relativizePath is a no-op unless the prefix is actually
    // present, so running it here costs nothing and covers absolute outliers.
    const key = parentFolderOfPath(relativizePath(row?.table || '', root));
    return {
      key,
      kind: AREA_KIND.FOLDER,
      label: areaLabel(key, AREA_KIND.FOLDER),
      ...areaParts(AREA_KIND.FOLDER, '', '', key),
    };
  }

  const table = row?.table || '';
  const schema = row?.schema || '';
  // Neither component is present (a shape we don't expect, but a blank area row would silently
  // swallow findings) — fall back to the raw location so the row stays visible and attributable.
  const key = table ? (schema ? `${schema}.${table}` : table) : (row?.location || '');
  return { key, kind, label: areaLabel(key, kind), ...areaParts(kind, schema, table, key) };
}

/**
 * Roll diff rows up into one entry per area.
 *
 * Counts are in the SOURCE'S unit (see countUnitForSource): distinct columns for a relational or
 * NoSQL source, total finding instances for a file source. They therefore add up to the page
 * header's KPIs rather than to the ledger's row count — `rowCount` keeps that, and `rows` keeps
 * the rows themselves, which is what selection and the handoff CSV work on.
 *
 * `openCount` is what a heading's "Select all" ticks, counted in the same unit: NEW + PERSISTING,
 * since RESOLVED findings are already gone and are never handed off.
 *
 * Returned sorted by new desc, then total desc, then label — the order the overview opens in.
 */
export function groupRowsByArea(rows, source, levelByEntity = {}) {
  const unit = countUnitForSource(source);
  const byKey = new Map();

  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const { key, kind, label, name, qualifier } = areaOf(row, source);
    let entry = byKey.get(key);
    if (!entry) {
      entry = {
        key,
        kind,
        label,
        // Name and qualifier (schema / database / parent path) kept apart for two-line rendering.
        name,
        qualifier,
        rows: [],
        entityCounts: new Map(),
        worstLevel: null,
      };
      byKey.set(key, entry);
    }
    entry.rows.push(row);

    // Entity mix for the overview's "top entities" cell — current counts, so a RESOLVED row
    // (countTo null → 0) doesn't inflate what is still there.
    const instances = row.countTo == null ? 0 : row.countTo;
    entry.entityCounts.set(row.entity, (entry.entityCounts.get(row.entity) || 0) + instances);

    const level = levelByEntity[String(row.entity).toUpperCase()];
    if (level && LEVEL_RANK[level] != null) {
      if (entry.worstLevel == null || LEVEL_RANK[level] < LEVEL_RANK[entry.worstLevel]) {
        entry.worstLevel = level;
      }
    }
  });

  return [...byKey.values()]
    .map((e) => ({
      ...e,
      rowCount: e.rows.length,
      total: countFindings(e.rows, unit),
      // One bucket per column (diffTotalsIn), so an area's new + persisting + resolved sums to
      // its total instead of double-counting a column that gained an entity and kept another.
      ...diffTotalsIn(e.rows, unit),
      openCount: countFindings(e.rows.filter(isRemediable), unit),
      topEntities: [...e.entityCounts.entries()]
        .map(([entity, count]) => ({ entity, count }))
        .sort((a, b) => b.count - a.count || String(a.entity).localeCompare(String(b.entity))),
    }))
    .sort((a, b) => b.new - a.new || b.total - a.total || String(a.label).localeCompare(String(b.label)));
}

/** Selection keys for every open (NEW/PERSISTING) row in an area — what "Select all" ticks. */
export function openRowsOfArea(area) {
  return (area?.rows || []).filter(isRemediable);
}

/**
 * Shorten a folder path for a tile face. Folder areas can be 90+ characters
 * ("sharepoint://host/sites/…/Documents/Development/testing/docs/HR"), which is unreadable at
 * tile size; the last two segments are what distinguishes them in practice. Table and collection
 * names are already short and pass through untouched. Call sites keep the full label in `title`.
 */
export function shortAreaLabel(area) {
  if (!area) return '';
  const label = area.label || '';
  if (area.kind !== AREA_KIND.FOLDER) return area.name || label;
  const parts = label.split('/').filter(Boolean);
  return parts.length <= 2 ? label : `…/${parts.slice(-2).join('/')}`;
}

/**
 * The qualifier as a tile / row second line: the schema, the NoSQL database, or the parent folder
 * path shortened from the left ("…/Documents/testing") so a deep path still fits. Empty string
 * when the area has nothing above it (an owner-less MySQL table, a folder at the scan root).
 */
export function areaQualifierLabel(area, { maxSegments = 2 } = {}) {
  if (!area) return '';
  const q = area.qualifier || '';
  if (!q || area.kind !== AREA_KIND.FOLDER) return q;
  const parts = q.split('/').filter(Boolean);
  return parts.length <= maxSegments ? q : `…/${parts.slice(-maxSegments).join('/')}`;
}

/**
 * Lay areas out for the map view, mirroring the home grid's `sizeTiles`: the area carrying the
 * most findings spans 3x2, the next 2x2, the rest 1x1 — so "where the problem is" is legible at
 * a glance rather than requiring a read down a column of numbers. Input order is preserved for
 * everything after the two feature tiles, so the caller's sort still drives the sequence.
 */
export function sizeAreaTiles(areas) {
  const list = Array.isArray(areas) ? areas : [];
  const byVolume = list.slice().sort((a, b) => b.total - a.total || String(a.label).localeCompare(String(b.label)));
  const first = byVolume[0];
  const second = byVolume[1];
  // Only promote when it actually reads as a hierarchy; three equal tiles is just noise.
  const promote = list.length > 2;
  return list.map((area) => ({
    area,
    span: promote && area === first ? '3x2' : promote && area === second ? '2x2' : '1x1',
  }));
}
